import { Box, Button, Paper } from '@mui/material';
import { AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import CommentAuthorSection from './commentAuthorSection';
import CommentReplySection from './commentReplySection';
import CommentTextField from './commentTextField';

import {
  axiosInstanceForPublicAPIWithAuth,
  axiosInstanceForTenantOnly,
} from '../../../assets/utils/axiosInstance';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import { Comment } from '../../../interfaces/Comment';
import { CommentReply } from '../../../interfaces/CommentReply';
import {
  CommentReplyResponse,
  CommentResponse,
} from '../../../interfaces/CommentResponse';
import { ErrorMessage } from '../../../interfaces/ErrorMessage';
import { RootState } from '../../../utils/store';

interface CommentCardProps {
  comment: Comment;
  isActive: boolean;
  setComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  deleteComment: (id: string) => void;
  resolveComment: (id: string) => void;
}

const CommentCard = ({
  comment,
  isActive,
  deleteComment,
  setComments,
  resolveComment,
}: CommentCardProps) => {
  const [isExpanded, setExpanded] = useState(false);
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const UNKNOWN_ERROR_MESSAGE = intl.formatMessage({
    defaultMessage:
      'An unknown issue ocurred while processing your comment request.',
  });

  const { id } = useSelector((state: RootState) => state.userProfile);
  const hasUnreadComment =
    comment.hasSeenUsers.find((userID) => userID === id) === undefined;

  const handleToggleExpandCard = () => {
    setExpanded((value) => !value);
  };

  const editComment = async (text: string, userIDs: number[]) => {
    if (text === comment.content) {
      return;
    }
    const commentBody = {
      content: text,
      tagged_users: userIDs,
      is_edited: true,
    };

    try {
      const response =
        await axiosInstanceForTenantOnly().patch<CommentResponse>(
          `program-policy/comments/${comment.id}`,
          commentBody,
        );
      if (response.status === StatusCodes.OK) {
        setComments((commentList) =>
          commentList.map((currentComment) => {
            if (currentComment.id === comment.id) {
              return {
                ...comment,
                content: response.data.content,
                taggedUsersDetails: response.data.tagged_users_details,
                isEdited: response.data.is_edited,
              };
            }
            return currentComment;
          }),
        );
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Comment updated.' }),
          'success',
        );
      }
    } catch (error: unknown) {
      const apiError = error as AxiosError<ErrorMessage>;
      createToastNotification(
        apiError.response
          ? apiError.response.data.message
          : UNKNOWN_ERROR_MESSAGE,
        'error',
      );
    }
  };

  const handleAddReply = async (text: string, userIDs: number[]) => {
    const commentBody = {
      content: text,
      tagged_users: userIDs,
    };

    try {
      const response =
        await axiosInstanceForTenantOnly().post<CommentReplyResponse>(
          `program-policy/comments/${parseInt(comment.id)}/reply`,
          commentBody,
        );
      if (response.status === StatusCodes.CREATED) {
        const processedReply: CommentReply = {
          id: response.data.id,
          updatedAt: response.data.updated_at,
          content: response.data.content,
          authorDetails: response.data.author_details,
          taggedUsersDetails: response.data.tagged_users_details,
          hasSeenUsers: response.data.has_seen_users,
          isEdited: response.data.is_edited,
        };

        setComments((commentList) =>
          commentList.map((existingComment) => {
            if (existingComment.id === comment.id) {
              return {
                ...existingComment,
                replies: [...existingComment.replies, processedReply],
              };
            } else {
              return existingComment;
            }
          }),
        );
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Reply added.' }),
          'success',
        );
      }
    } catch (error: unknown) {
      const apiError = error as AxiosError<ErrorMessage>;
      createToastNotification(
        apiError.response
          ? apiError.response.data.message
          : UNKNOWN_ERROR_MESSAGE,
        'error',
      );
    }
  };

  const handleDeleteReply = async (deletedReplyId: number) => {
    try {
      const response =
        await axiosInstanceForTenantOnly().delete<CommentReplyResponse>(
          `program-policy/comments/reply/${deletedReplyId}`,
        );
      if (response.status === StatusCodes.NO_CONTENT) {
        setComments((commentList) =>
          commentList.map((existingComment) => {
            if (existingComment.id === comment.id) {
              const filteredReplies = existingComment.replies.filter(
                (reply) => reply.id !== deletedReplyId,
              );
              return {
                ...existingComment,
                replies: filteredReplies,
              };
            } else {
              return existingComment;
            }
          }),
        );
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Reply deleted.' }),
          'success',
        );
      }
    } catch (error: unknown) {
      const apiError = error as AxiosError<ErrorMessage>;
      createToastNotification(
        apiError.response
          ? apiError.response.data.message
          : UNKNOWN_ERROR_MESSAGE,
        'error',
      );
    }
  };

  const handleEditReply = async (
    replyID: number,
    text: string,
    userIDs: number[],
  ) => {
    const commentBody = {
      content: text,
      tagged_users: userIDs,
      is_edited: true,
    };

    try {
      const response =
        await axiosInstanceForTenantOnly().patch<CommentReplyResponse>(
          `program-policy/comments/reply/${replyID}`,
          commentBody,
        );
      if (response.status === StatusCodes.OK) {
        const currentCommentReplies = comment.replies;
        const updatedReplies = currentCommentReplies.map((commentReply) => {
          if (commentReply.id === replyID) {
            return {
              ...commentReply,
              content: response.data.content,
              hasSeenUsers: response.data.has_seen_users,
              updatedAt: response.data.updated_at,
              taggedUsersDetails: response.data.tagged_users_details,
              isEdited: response.data.is_edited,
            };
          }
          return commentReply;
        });
        setComments((commentList) =>
          commentList.map((currentComment) => {
            if (currentComment.id === comment.id) {
              return {
                ...comment,
                replies: updatedReplies,
              };
            }
            return currentComment;
          }),
        );
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Reply updated.' }),
          'success',
        );
      }
    } catch (error: unknown) {
      const apiError = error as AxiosError<ErrorMessage>;
      createToastNotification(
        apiError.response
          ? apiError.response.data.message
          : UNKNOWN_ERROR_MESSAGE,
        'error',
      );
    }
  };

  const markCommentAsRead = async () => {
    try {
      const response =
        await axiosInstanceForPublicAPIWithAuth().patch<CommentResponse>(
          `update-seen-users-comment/${comment.id}`,
        );
      if (response.status === StatusCodes.OK) {
        setComments((commentList) =>
          commentList.map((currentComment) => {
            if (currentComment.id === comment.id) {
              return {
                ...currentComment,
                hasSeenUsers: response.data.has_seen_users,
              };
            }
            return currentComment;
          }),
        );
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Comment updated.' }),
          'success',
        );
      }
    } catch (error: unknown) {
      const apiError = error as AxiosError<ErrorMessage>;
      createToastNotification(
        apiError.response
          ? apiError.response.data.message
          : UNKNOWN_ERROR_MESSAGE,
        'error',
      );
    }
  };

  const markReplyAsRead = async (replyID: number) => {
    try {
      const response =
        await axiosInstanceForPublicAPIWithAuth().patch<CommentResponse>(
          `update-seen-users-reply/${replyID}`,
        );
      if (response.status === StatusCodes.OK) {
        const currentCommentReplies = comment.replies;
        const updatedReplies = currentCommentReplies.map((commentReply) => {
          if (commentReply.id === replyID) {
            return {
              ...commentReply,
              hasSeenUsers: response.data.has_seen_users,
            };
          }
          return commentReply;
        });
        setComments((commentList) =>
          commentList.map((currentComment) => {
            if (currentComment.id === comment.id) {
              return {
                ...comment,
                replies: updatedReplies,
              };
            }
            return currentComment;
          }),
        );
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Reply updated.' }),
          'success',
        );
      }
    } catch (error: unknown) {
      const apiError = error as AxiosError<ErrorMessage>;
      createToastNotification(
        apiError.response
          ? apiError.response.data.message
          : UNKNOWN_ERROR_MESSAGE,
        'error',
      );
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          padding: '10px 16px 16px',
          borderRadius: '6px',
          border: '1px',
          borderStyle: 'solid',
          borderColor: isActive ? 'blue' : '#D1D1D1',
          backgroundColor: hasUnreadComment ? '#ECF5FF' : 'white',
        }}
      >
        <Box
          sx={{
            maxHeight: '350px',
            overflowY: 'auto',
          }}
        >
          <CommentAuthorSection
            comment={comment}
            isUnread={hasUnreadComment}
            isExpanded={isExpanded}
            resolveComment={resolveComment}
            deleteComment={deleteComment}
            editComment={editComment}
            markAsRead={markCommentAsRead}
          />

          {isExpanded &&
            comment.replies.length > 0 &&
            comment.replies.map((reply) => (
              <CommentReplySection
                reply={reply}
                key={reply.id}
                deleteReply={handleDeleteReply}
                editReply={handleEditReply}
                markAsRead={markReplyAsRead}
              />
            ))}
        </Box>
        {isExpanded && (
          <CommentTextField
            handleAddComment={handleAddReply}
            handleCancelComment={() => {}}
            replyMode
          />
        )}
        <Button
          variant='text'
          color='darkGray'
          sx={{
            textTransform: 'none',
          }}
          onClick={handleToggleExpandCard}
        >
          {isExpanded ? (
            <FormattedMessage defaultMessage='Collapse' />
          ) : (
            <FormattedMessage
              defaultMessage='Expand ({replyCount, plural, =0 {0 replies} one {# reply} other {# replies}})'
              values={{
                replyCount: comment.replies.length,
              }}
            />
          )}
        </Button>
      </Paper>
    </>
  );
};

export default CommentCard;
